import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import BackdropOverlay, {
    DIALOG_OVERLAY_TOP_OFFSET,
} from "../common/BackdropOverlay.jsx";
import { colors } from "../../theme/colors";
import { DIALOG_PAPER_RADIUS } from "../common/dialogActionButtonSx";
import {
    SCROLLBAR_THUMB_COLOR,
    SCROLLBAR_THUMB_HOVER_COLOR,
    SCROLLBAR_TRACK_COLOR,
} from "../../universal/scrollbarStyles";
import { OURO_CODE_INTRO, OURO_CODE_PRINCIPLES } from "./ouroCode";

const MotionBox = motion(Box);

const DIALOG_Z_INDEX = 1500;

const SCROLLABLE_CONTENT_SX = {
    overflowY: "auto",
    overflowX: "hidden",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "thin",
    scrollbarColor: `${SCROLLBAR_THUMB_COLOR} ${SCROLLBAR_TRACK_COLOR}`,
    maxHeight: { xs: "340px", sm: "400px" },
    "&::-webkit-scrollbar": {
        width: "8px",
    },
    "&::-webkit-scrollbar-track": {
        background: SCROLLBAR_TRACK_COLOR,
        borderRadius: "999px",
    },
    "&::-webkit-scrollbar-thumb": {
        background: SCROLLBAR_THUMB_COLOR,
        border: "2px solid transparent",
        backgroundClip: "padding-box",
        borderRadius: "999px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
        background: SCROLLBAR_THUMB_HOVER_COLOR,
    },
};

const CAPSULE_SX = {
    flexShrink: 0,
    px: { xs: "0.55rem", sm: "0.75rem" },
    py: { xs: "0.3rem", sm: "0.35rem" },
    borderRadius: "999px",
    background: `
        linear-gradient(
            180deg,
            rgba(232,163,61,0.14),
            rgba(232,163,61,0.06)
        )
    `,
    border: `1px solid ${alpha(colors.primary.main, 0.24)}`,
    color: colors.primary.main,
    fontSize: { xs: "9px", sm: "10px" },
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    lineHeight: 1,
    whiteSpace: "nowrap",
    pointerEvents: "none",
};

function formatPrincipleIndex(index) {
    return String(index + 1).padStart(2, "0");
}

function formatPrincipleTitle(title) {
    return String(title || "")
        .replace(/\.\s*$/, "")
        .trim()
        .toUpperCase();
}

export default function OuroCodeDialog({ open, onAccept, onClose }) {
    if (!open || typeof document === "undefined") {
        return null;
    }

    return ReactDOM.createPortal(
        <BackdropOverlay
            open={open}
            onClick={onClose}
            zIndex={DIALOG_Z_INDEX}
            sx={{
                inset: 0,
                width: "100%",
                height: "100dvh",
                minHeight: "100dvh",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                pt: DIALOG_OVERLAY_TOP_OFFSET,
                pb: { xs: 2, sm: 3 },
                boxSizing: "border-box",
            }}
        >
            <MotionBox
                role="dialog"
                aria-labelledby="ouro-code-title"
                aria-modal="true"
                onClick={(event) => event.stopPropagation()}
                initial={{
                    opacity: 0,
                    scale: 0.96,
                    y: 16,
                }}
                animate={{
                    opacity: 1,
                    scale: 1,
                    y: 0,
                }}
                transition={{
                    duration: 0.22,
                    ease: [0.22, 1, 0.36, 1],
                }}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: 680,
                    maxWidth: "92vw",
                    height: "auto",
                    maxHeight: "calc(100dvh - 48px)",
                    borderRadius: DIALOG_PAPER_RADIUS,
                    overflow: "hidden",
                    border: `1px solid ${alpha(colors.primary.main, 0.18)}`,
                    background: `
                        linear-gradient(
                            180deg,
                            rgba(28,27,26,.98),
                            rgba(18,18,19,.98)
                        )
                    `,
                    boxShadow: `
                        0 30px 80px rgba(0,0,0,.55),
                        inset 0 1px 0 rgba(255,255,255,.03)
                    `,
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        flexShrink: 0,
                        pt: { xs: "24px", sm: "32px" },
                        pb: { xs: "20px", sm: "26px" },
                        px: { xs: "20px", sm: "32px" },
                        background: `
                            linear-gradient(
                                180deg,
                                rgba(232,163,61,.09),
                                transparent
                            )
                        `,
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "1px",
                            background: `
                                linear-gradient(
                                    90deg,
                                    transparent,
                                    rgba(232,163,61,.45),
                                    transparent
                                )
                            `,
                        }}
                    />

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "stretch", sm: "flex-start" },
                            justifyContent: "space-between",
                            gap: { xs: "12px", sm: "18px" },
                        }}
                    >
                        <Box
                            sx={{
                                display: { xs: "flex", sm: "none" },
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "10px",
                                    letterSpacing: "0.16em",
                                    textTransform: "uppercase",
                                    color: colors.primary.main,
                                    fontWeight: 700,
                                    minWidth: 0,
                                }}
                            >
                                Community Guidelines
                            </Typography>
                            <Box sx={CAPSULE_SX}>Community</Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: { xs: "12px", sm: "18px" },
                                minWidth: 0,
                                flex: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: 40, sm: 52 },
                                    height: { xs: 40, sm: 52 },
                                    borderRadius: { xs: "12px", sm: "16px" },
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    background: `
                                        linear-gradient(
                                            180deg,
                                            rgba(232,163,61,.18),
                                            rgba(232,163,61,.06)
                                        )
                                    `,
                                    border: "1px solid rgba(232,163,61,.25)",
                                    boxShadow: "inset 0 1px 0 rgba(255,255,255,.05)",
                                }}
                            >
                                <VerifiedOutlinedIcon
                                    sx={{
                                        color: colors.primary.main,
                                        fontSize: { xs: "1.2rem", sm: "1.45rem" },
                                    }}
                                />
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        display: { xs: "none", sm: "block" },
                                        fontSize: "11px",
                                        letterSpacing: "0.18em",
                                        textTransform: "uppercase",
                                        color: colors.primary.main,
                                        fontWeight: 700,
                                        mb: "0.45rem",
                                    }}
                                >
                                    Community Guidelines
                                </Typography>
                                <Typography
                                    id="ouro-code-title"
                                    component="h2"
                                    sx={{
                                        fontSize: { xs: "1.45rem", sm: "34px" },
                                        fontWeight: 700,
                                        letterSpacing: "-0.03em",
                                        color: colors.text.primary,
                                        lineHeight: 1.1,
                                    }}
                                >
                                    The Ouro Code
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                ...CAPSULE_SX,
                                display: { xs: "none", sm: "block" },
                                mt: "4px",
                            }}
                        >
                            Community
                        </Box>
                    </Box>
                </Box>

                <Box
                    data-lenis-prevent
                    data-lenis-prevent-wheel
                    data-lenis-prevent-touch
                    sx={{
                        ...SCROLLABLE_CONTENT_SX,
                        px: { xs: "20px", sm: "32px" },
                        pb: { xs: "20px", sm: "28px" },
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "16px",
                            lineHeight: 1.8,
                            maxWidth: "520px",
                            color: colors.text.secondary,
                            mb: "24px",
                        }}
                    >
                        {OURO_CODE_INTRO}
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {OURO_CODE_PRINCIPLES.map((principle, index) => (
                            <Box
                                key={principle.title}
                                sx={{
                                    display: "flex",
                                    gap: "14px",
                                    alignItems: "flex-start",
                                    p: "18px",
                                    borderRadius: "12px",
                                    background: "rgba(255,255,255,.015)",
                                    border: "1px solid rgba(255,255,255,.05)",
                                    transition:
                                        "transform 250ms ease, background 250ms ease, border-color 250ms ease",
                                    "&:hover": {
                                        transform: "translateX(4px)",
                                        background: "rgba(232,163,61,.05)",
                                        borderColor: "rgba(232,163,61,.18)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        background: "rgba(232,163,61,.12)",
                                        border: "1px solid rgba(232,163,61,.18)",
                                        fontFamily: "'JetBrains Mono', monospace",
                                        fontWeight: 700,
                                        fontSize: "11px",
                                        color: colors.primary.main,
                                        lineHeight: 1,
                                    }}
                                >
                                    {formatPrincipleIndex(index)}
                                </Box>

                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            textTransform: "uppercase",
                                            letterSpacing: "0.08em",
                                            fontWeight: 700,
                                            fontSize: "13px",
                                            color: colors.text.primary,
                                            mb: "0.35rem",
                                        }}
                                    >
                                        {formatPrincipleTitle(principle.title)}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            lineHeight: 1.7,
                                            color: "rgba(255,255,255,.72)",
                                        }}
                                    >
                                        {principle.body}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box
                    sx={{
                        flexShrink: 0,
                        height: "1px",
                        mx: { xs: "20px", sm: "32px" },
                        background: `
                            linear-gradient(
                                90deg,
                                transparent,
                                rgba(255,255,255,.08),
                                transparent
                            )
                        `,
                    }}
                />

                <Box
                    sx={{
                        flexShrink: 0,
                        px: { xs: "20px", sm: "32px" },
                        py: { xs: "18px", sm: "24px" },
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "12px",
                            color: colors.text.muted,
                            textAlign: "center",
                            lineHeight: 1.5,
                        }}
                    >
                        By continuing you agree to uphold these principles while using Ouro.
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "12px",
                        }}
                    >
                    <Button
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            onClose();
                        }}
                            sx={{
                                height: "42px",
                                px: "18px",
                                borderRadius: "10px",
                                background: "rgba(255,255,255,.03)",
                                border: "1px solid rgba(255,255,255,.06)",
                                color: colors.text.secondary,
                                textTransform: "none",
                                fontWeight: 600,
                                boxShadow: "none",
                                "&:hover": {
                                    background: "rgba(255,255,255,.05)",
                                    boxShadow: "none",
                                },
                            }}
                        >
                            Not now
                        </Button>
                    <Button
                        type="button"
                        onPointerDown={(event) => {
                            // Accept on pointerdown so the gesture can't fall through
                            // onto PLAY after the dialog unmounts (classic modal bug).
                            if (event.button != null && event.button !== 0) {
                                return;
                            }
                            event.preventDefault();
                            event.stopPropagation();
                            onAccept();
                        }}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onAccept();
                        }}
                            sx={{
                                height: "42px",
                                px: "22px",
                                borderRadius: "10px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: colors.text.contrast,
                                background: `
                                    linear-gradient(
                                        180deg,
                                        #F1B24B,
                                        #D89128
                                    )
                                `,
                                border: "1px solid rgba(255,255,255,.08)",
                                boxShadow: `
                                    0 10px 20px rgba(232,163,61,.15),
                                    inset 0 1px 0 rgba(255,255,255,.15)
                                `,
                                transition: "transform 180ms ease, filter 180ms ease",
                                "&:hover": {
                                    transform: "translateY(-1px)",
                                    filter: "brightness(1.04)",
                                    background: `
                                        linear-gradient(
                                            180deg,
                                            #F1B24B,
                                            #D89128
                                        )
                                    `,
                                    boxShadow: `
                                        0 10px 20px rgba(232,163,61,.15),
                                        inset 0 1px 0 rgba(255,255,255,.15)
                                    `,
                                },
                            }}
                        >
                            I&apos;m in
                        </Button>
                    </Box>
                </Box>
            </MotionBox>
        </BackdropOverlay>,
        document.body,
    );
}

OuroCodeDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onAccept: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};
